import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Tooltip,
  IconButton,
  Paper,
  Fade,
  Grow,
  useTheme,
  useMediaQuery
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import { TransitionGroup } from 'react-transition-group';
import SettingExcelExport from './Exports/SettingExcelExport';
import SettingPdfExport from './Exports/SettingPdfExport';
import SettingJsonExport from './Exports/SettingJsonExport';
import SettingCsvExport from './Exports/SettingCsvExport';
import SettingDownloadFiles from './Exports/SettingDownloadFiles';

const SettingDataExport = ({ caseid }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isVisible, setIsVisible] = useState(false);

  // Trigger animation when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Define all export components in an array for easier animation mapping
  const exportComponents = [
    { Component: SettingJsonExport, props: { caseid } },
    { Component: SettingExcelExport, props: { caseid } },
    { Component: SettingPdfExport, props: { caseid } },
    { Component: SettingCsvExport, props: { caseid } },
    { Component: SettingDownloadFiles, props: { caseid } },
  ];

  return (
    <>
      <Fade in={true} timeout={600}>
        <Box sx={{ mb: { xs: 1, sm: 2 } }}>
          <Typography variant={isMobile ? "h6" : "h5"} gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            {t("settings.data_export")}
            <Tooltip title={t("settings.instant_data_export_tooltip")}>
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
        </Box>
      </Fade>

      <Fade in={isVisible} timeout={800}>
        <Paper elevation={0} sx={{ p: { xs: 1, sm: 2 }, backgroundColor: 'transparent' }}>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="stretch"
            gap={{ xs: 1, sm: 0 }}
            flexWrap="wrap"
            sx={{
              width: '100%',
              minHeight: exportComponents.length > 0 ? 'auto' : '200px',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '& > *': {
                display: 'flex',
                alignItems: 'stretch',
              }
            }}
          >
            <TransitionGroup component={null}>
              {exportComponents.map((exportComp, index) => {
                const { Component, props } = exportComp;
                return (
                  <Grow
                    key={`${Component.name}-${index}`}
                    in={isVisible}
                    timeout={{
                      enter: 500 + (index * 150), // Stagger animation
                      exit: 300,
                    }}
                    style={{
                      transformOrigin: '0 0',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        width: '120px',
                        minHeight: '120px',
                        animation: `slideInUp 0.7s ease-out ${index * 0.1}s both`,
                        '@keyframes slideInUp': {
                          '0%': {
                            opacity: 0,
                            transform: 'translateY(30px) scale(0.8)',
                          },
                          '60%': {
                            opacity: 0.8,
                            transform: 'translateY(-3px) scale(1.05)',
                          },
                          '100%': {
                            opacity: 1,
                            transform: 'translateY(0px) scale(1)',
                          },
                        },
                        '& > *': {
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }
                      }}
                    >
                      <Component {...props} />
                    </Box>
                  </Grow>
                );
              })}
            </TransitionGroup>
          </Box>
        </Paper>
      </Fade>
    </>
  );
};

export default SettingDataExport;
